import { ENTRY_POLICY_ACTIONS } from "../../../../util/src/custom-disciplines/entry-policy-contract.js";
import { isTimerAction } from "../../../../util/src/behavior-timers/protocol";
import { TimerActionNode } from "../timers/TimerActionNode";
import type {
  ActionSetKey,
  ActionSetNode as ActionSetNodeType,
  LayoutListTargetValue,
  ListValue,
  LocationValue,
  NodePortValue,
  Port,
  TransformValue,
} from "../../../../util/src/blueprint/types.js";
import {
  EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY,
  type EditorStyleActionKey,
} from "../../../../util/src/blueprint/editor-style-actions.js";
import dico from "../../../../util/src/language/dico.js";
import { Menu, MenuItem, TextField } from "@mui/material";
import { PortHandle } from "../PortHandle.js";
import { useMemo, useState } from "react";
import type { MouseEvent } from "react";
import type { BorderConfig } from "../../components/BorderPicker.js";
import type { BorderRadiusConfig } from "../../components/BorderRadiusPicker.js";
import type { SpacingConfig } from "../../components/SpacingPicker.js";

type CompositePortMenuTarget = "value" | "split";

interface CompositePortMenuState {
  mouseX: number;
  mouseY: number;
  target: CompositePortMenuTarget;
}

const LOCATION_PORT_IDS = ["value.x", "value.y", "value.z"] as const;
const TRANSFORM_PORT_IDS = [
  "value.width",
  "value.height",
  "value.rotation",
] as const;

function getStylePropertyLabel(key: EditorStyleActionKey): string {
  const behaviorActions = dico().behavior.actions as Record<string, unknown>;
  const styleNodeLabels =
    (behaviorActions.styleNodeLabels as Record<string, string> | undefined) ??
    {};
  const override = styleNodeLabels[key];
  if (typeof override === "string" && override.trim().length > 0) {
    return override;
  }
  const p = dico().licensedPortal.layoutEditor.properties as unknown as Record<
    string,
    string
  >;
  return p[EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY[key]] ?? key;
}

function getActionLabel(key: ActionSetKey): string {
  const behaviorDico = dico().behavior;
  if (isTimerAction(key)) return behaviorDico.timers[key];
  if (key === "text") return behaviorDico.actions.setText;
  if (key === "location") return behaviorDico.actions.setLocation;
  if (key === "transform") return behaviorDico.actions.setTransform;
  if (key === "setFilter") return behaviorDico.actions.setFilter;
  if (key === "setSort") return behaviorDico.actions.setSort;
  if (key === "setListSource") return behaviorDico.actions.setListSource;
  if (key === "setBinding") return behaviorDico.actions.setBinding;
  if (key === "iconColor") return behaviorDico.actions.iconColor;
  if (key === "iconSize") return behaviorDico.actions.iconSize;
  if (key === "customActionDisabled")
    return behaviorDico.actions.customActionDisabled;
  if (key === "customActionIcon") return behaviorDico.actions.customActionIcon;
  if (key === "customActionStartIcon")
    return behaviorDico.actions.customActionStartIcon;
  if (key === "customActionVariant")
    return behaviorDico.actions.customActionVariant;
  if (key === "customActionSize") return behaviorDico.actions.customActionSize;
  if (key === "customActionColor")
    return behaviorDico.actions.customActionColor;
  if (key === "customActionTooltip")
    return behaviorDico.actions.customActionTooltip;
  if (key === "customActionLabel")
    return behaviorDico.actions.customActionLabel;
  if (key === "customListSetField")
    return behaviorDico.actions.customListSetField;
  if (key === "customListSetRows")
    return behaviorDico.actions.customListSetRows;
  if (key === "customListSetViewRows")
    return behaviorDico.actions.customListSetViewRows;
  if (key === "customGroupSetField")
    return behaviorDico.actions.customGroupSetField;
  if (key === "customOpenGroup") return behaviorDico.actions.customOpenGroup;
  if (key === "customSetCurrentGroup")
    return behaviorDico.actions.customSetCurrentGroup;
  if (key === "customSetCurrentAthlete")
    return behaviorDico.actions.customSetCurrentAthlete;
  if (key === "customOpenModal") return behaviorDico.actions.customOpenModal;
  if (key === "customIncrementAthleteCount")
    return behaviorDico.actions.customIncrementAthleteCount;
  if (key === "customGroupService")
    return behaviorDico.actions.customGroupService;
  if (key === "customSubmitForm") return behaviorDico.actions.customSubmitForm;
  if (key === "customRunAction") return behaviorDico.actions.customRunAction;
  if (key === "customPrintModal") return behaviorDico.actions.customPrintModal;
  if (key === "customCloseModal") return behaviorDico.actions.customCloseModal;
  if (key === "customInputOptions")
    return behaviorDico.actions.customInputOptions;
  if (key in ENTRY_POLICY_ACTIONS)
    return (behaviorDico.actions as unknown as Record<string, string>)[key];
  if (key === "customReferenceFilter")
    return behaviorDico.actions.customReferenceFilter;
  if (key === "totalWeight")
    return behaviorDico.actions.setBarLoaderTotalWeight;
  if (key === "barWeight") return behaviorDico.actions.setBarLoaderBarWeight;
  return getStylePropertyLabel(key as EditorStyleActionKey);
}

function isCompositeActionKey(
  key: ActionSetKey,
): key is "location" | "transform" {
  return key === "location" || key === "transform";
}

function getPortNumberValue(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
}

export interface ActionSetNodeProps {
  node: ActionSetNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (
    nodeId: string,
    portId: string,
    value:
      | string
      | number
      | boolean
      | ListValue
      | NodePortValue
      | LocationValue
      | TransformValue
      | BorderConfig
      | BorderRadiusConfig
      | SpacingConfig
      | LayoutListTargetValue
      | unknown,
  ) => void;
  onToggleExpandedPorts?: (nodeId: string) => void;
}

/**
 * Generic behavior action node that sets a target field when the flow runs through it.
 */
const StandardActionSetNode = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
  onToggleExpandedPorts,
}: ActionSetNodeProps) => {
  const behaviorDico = dico().behavior;
  const [portMenuState, setPortMenuState] =
    useState<CompositePortMenuState | null>(null);
  const splitPortIds = useMemo(
    () => (node.key === "location" ? LOCATION_PORT_IDS : TRANSFORM_PORT_IDS),
    [node.key],
  );

  const inPort = node.ports.find((p) => p.id === "in");
  const outPort = node.ports.find((p) => p.id === "out");
  if (!inPort || !outPort) return null;

  const isComposite = isCompositeActionKey(node.key);
  const valuePort = node.ports.find((p) => p.id === "value");
  const targetPort = node.ports.find((p) => p.id === "target");
  const extraInputPorts = node.ports.filter(
    (port) =>
      port.direction === "input" &&
      port.id !== "in" &&
      port.id !== "value" &&
      port.id !== "target" &&
      !LOCATION_PORT_IDS.includes(
        port.id as (typeof LOCATION_PORT_IDS)[number],
      ) &&
      !TRANSFORM_PORT_IDS.includes(
        port.id as (typeof TRANSFORM_PORT_IDS)[number],
      ),
  );
  const splitPorts = splitPortIds
    .map((portId) => node.ports.find((p) => p.id === portId))
    .filter((port): port is Port => Boolean(port));
  const isExpandedComposite = isComposite && splitPorts.length === 3;

  const collapsedCompositeValue = ((valuePort?.value as
    | LocationValue
    | TransformValue
    | undefined) ?? {}) as Partial<LocationValue & TransformValue>;
  const compositeInputSx = {
    width: 68,
    "& .MuiInputBase-input": {
      py: 0.4,
      px: 0.9,
      fontSize: 12,
    },
  };

  const handleCompositePortContextMenu = (
    event: MouseEvent,
    target: CompositePortMenuTarget,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setPortMenuState({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      target,
    });
  };

  const closePortMenu = () => setPortMenuState(null);

  const renderCollapsedCompositeInputs = () => {
    if (!valuePort) return null;
    if (isPortConnected?.(node.id, valuePort.id)) return null;

    if (node.key === "location") {
      return (
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}
          onContextMenu={(event) => {
            handleCompositePortContextMenu(event, "value");
          }}
        >
          <TextField
            size="small"
            type="number"
            value={getPortNumberValue(collapsedCompositeValue.x)}
            onMouseDown={(event) => event.stopPropagation()}
            onChange={(event) =>
              onPortValueChange?.(node.id, valuePort.id, {
                x: Number(event.target.value),
                y: getPortNumberValue(collapsedCompositeValue.y),
                z: getPortNumberValue(collapsedCompositeValue.z),
              })
            }
            sx={compositeInputSx}
          />
          <TextField
            size="small"
            type="number"
            value={getPortNumberValue(collapsedCompositeValue.y)}
            onMouseDown={(event) => event.stopPropagation()}
            onChange={(event) =>
              onPortValueChange?.(node.id, valuePort.id, {
                x: getPortNumberValue(collapsedCompositeValue.x),
                y: Number(event.target.value),
                z: getPortNumberValue(collapsedCompositeValue.z),
              })
            }
            sx={compositeInputSx}
          />
          <TextField
            size="small"
            type="number"
            value={getPortNumberValue(collapsedCompositeValue.z)}
            onMouseDown={(event) => event.stopPropagation()}
            onChange={(event) =>
              onPortValueChange?.(node.id, valuePort.id, {
                x: getPortNumberValue(collapsedCompositeValue.x),
                y: getPortNumberValue(collapsedCompositeValue.y),
                z: Number(event.target.value),
              })
            }
            sx={compositeInputSx}
          />
        </div>
      );
    }

    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}
        onContextMenu={(event) => {
          handleCompositePortContextMenu(event, "value");
        }}
      >
        <TextField
          size="small"
          type="number"
          value={getPortNumberValue(collapsedCompositeValue.width)}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) =>
            onPortValueChange?.(node.id, valuePort.id, {
              width: Number(event.target.value),
              height: getPortNumberValue(collapsedCompositeValue.height),
              rotation: getPortNumberValue(collapsedCompositeValue.rotation),
            })
          }
          sx={compositeInputSx}
        />
        <TextField
          size="small"
          type="number"
          value={getPortNumberValue(collapsedCompositeValue.height)}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) =>
            onPortValueChange?.(node.id, valuePort.id, {
              width: getPortNumberValue(collapsedCompositeValue.width),
              height: Number(event.target.value),
              rotation: getPortNumberValue(collapsedCompositeValue.rotation),
            })
          }
          sx={compositeInputSx}
        />
        <TextField
          size="small"
          type="number"
          value={getPortNumberValue(collapsedCompositeValue.rotation)}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) =>
            onPortValueChange?.(node.id, valuePort.id, {
              width: getPortNumberValue(collapsedCompositeValue.width),
              height: getPortNumberValue(collapsedCompositeValue.height),
              rotation: Number(event.target.value),
            })
          }
          sx={compositeInputSx}
        />
      </div>
    );
  };

  return (
    <div
      style={{
        minWidth: 310,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background:
            "linear-gradient(180deg, #5eead4 0%, #0d9488 55%, #0f766e 100%)",
        }}
      >
        <span>{behaviorDico.actions.setHeader}</span>
        <span style={{ fontWeight: 600, opacity: 0.95 }}>
          {getActionLabel(node.key)}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "8px",
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <PortHandle
            node={node}
            port={inPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, inPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, inPort)}
            isConnected={isPortConnected?.(node.id, inPort.id) ?? false}
          />
          {!isComposite && valuePort ? (
            <>
              <PortHandle
                node={node}
                port={valuePort}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, valuePort)}
                onMouseUp={() => onPortMouseUp?.(node.id, valuePort)}
                showValueInput
                onValueChange={(value) =>
                  onPortValueChange?.(node.id, valuePort.id, value)
                }
                isConnected={isPortConnected?.(node.id, valuePort.id) ?? false}
              />
              {targetPort && (
                <PortHandle
                  node={node}
                  port={targetPort}
                  setPortsMap={setPortsMap}
                  onMouseDown={() => onPortMouseDown?.(node.id, targetPort)}
                  onMouseUp={() => onPortMouseUp?.(node.id, targetPort)}
                  isConnected={
                    isPortConnected?.(node.id, targetPort.id) ?? false
                  }
                />
              )}
              {extraInputPorts.map((port) => (
                <PortHandle
                  key={port.id}
                  node={node}
                  port={port}
                  setPortsMap={setPortsMap}
                  onMouseDown={() => onPortMouseDown?.(node.id, port)}
                  onMouseUp={() => onPortMouseUp?.(node.id, port)}
                  showValueInput
                  onValueChange={(value) =>
                    onPortValueChange?.(node.id, port.id, value)
                  }
                  isConnected={isPortConnected?.(node.id, port.id) ?? false}
                />
              ))}
            </>
          ) : isExpandedComposite ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                width: "100%",
              }}
            >
              {splitPorts.map((port) => (
                <div
                  key={port.id}
                  style={{ width: "100%" }}
                  onContextMenu={(event) =>
                    handleCompositePortContextMenu(event, "split")
                  }
                >
                  <PortHandle
                    node={node}
                    port={port}
                    setPortsMap={setPortsMap}
                    onMouseDown={() => onPortMouseDown?.(node.id, port)}
                    onMouseUp={() => onPortMouseUp?.(node.id, port)}
                    showValueInput
                    onValueChange={(value) =>
                      onPortValueChange?.(
                        node.id,
                        port.id,
                        typeof value === "number" ? value : Number(value),
                      )
                    }
                    isConnected={isPortConnected?.(node.id, port.id) ?? false}
                    onContextMenu={(event) =>
                      handleCompositePortContextMenu(event, "split")
                    }
                  />
                </div>
              ))}
            </div>
          ) : valuePort ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
              }}
              onContextMenu={(event) =>
                handleCompositePortContextMenu(event, "value")
              }
            >
              <PortHandle
                node={node}
                port={valuePort}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, valuePort)}
                onMouseUp={() => onPortMouseUp?.(node.id, valuePort)}
                isConnected={isPortConnected?.(node.id, valuePort.id) ?? false}
                onContextMenu={(event) =>
                  handleCompositePortContextMenu(event, "value")
                }
              />
              {renderCollapsedCompositeInputs()}
            </div>
          ) : (
            extraInputPorts.map((port) => (
              <PortHandle
                key={port.id}
                node={node}
                port={port}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, port)}
                onMouseUp={() => onPortMouseUp?.(node.id, port)}
                showValueInput
                onValueChange={(value) =>
                  onPortValueChange?.(node.id, port.id, value)
                }
                isConnected={isPortConnected?.(node.id, port.id) ?? false}
              />
            ))
          )}
          {isComposite && targetPort && (
            <PortHandle
              node={node}
              port={targetPort}
              setPortsMap={setPortsMap}
              onMouseDown={() => onPortMouseDown?.(node.id, targetPort)}
              onMouseUp={() => onPortMouseUp?.(node.id, targetPort)}
              isConnected={isPortConnected?.(node.id, targetPort.id) ?? false}
            />
          )}
        </div>

        <div style={{ flexShrink: 0 }}>
          <PortHandle
            node={node}
            port={outPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, outPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, outPort)}
            isConnected={isPortConnected?.(node.id, outPort.id) ?? false}
          />
        </div>
      </div>

      <Menu
        open={portMenuState != null}
        onClose={closePortMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          portMenuState != null
            ? { top: portMenuState.mouseY, left: portMenuState.mouseX }
            : undefined
        }
      >
        {portMenuState?.target === "value" ? (
          <MenuItem
            onClick={() => {
              onToggleExpandedPorts?.(node.id);
              closePortMenu();
            }}
          >
            {behaviorDico.actions.splitPorts}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              onToggleExpandedPorts?.(node.id);
              closePortMenu();
            }}
          >
            {behaviorDico.actions.joinPorts}
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export const ActionSetNode = (props: ActionSetNodeProps) =>
  isTimerAction(props.node.key) ? (
    <TimerActionNode {...props} />
  ) : (
    <StandardActionSetNode {...props} />
  );
